import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Circle, Clock, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const STAGES = [
  ["preparing", "Preparing request"],
  ["checking", "Validating SAP tax"],
  ["creating", "Creating SAP draft"],
  ["readback", "Confirming draft & employee"],
  ["plant", "Verifying approved Plant"],
  ["grn", "Confirming selected GRN"],
  ["reconcile", "Pinning GRN sub-items"],
  ["complete", "Ready for review"],
];

const formatElapsed = (startedAt, endedAt) => {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  const totalSec = Math.max(0, Math.floor((end - start) / 1000));
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

export const SapDraftProgress = ({ progress }) => {
  const isError = progress?.state === "error";
  const isComplete = progress?.state === "complete";
  // Live tick while active; freeze when terminal.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!progress || isError || isComplete) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [progress, isError, isComplete]);

  if (!progress) return null;
  const activeIndex = Math.max(0, STAGES.findIndex(([stage]) => stage === progress.stage));
  // Proportional progress: each stage = 100/8 = 12.5%. Step 6 of 8 → 75%, not 96%.
  const perStagePct = 100 / STAGES.length;
  const value = isError ? Math.max(perStagePct, activeIndex * perStagePct) : isComplete ? 100 : Math.max(perStagePct, (activeIndex + 1) * perStagePct);
  const elapsed = formatElapsed(progress.started_at, isComplete || isError ? progress.updated_at : null);

  return <section className={`border-l-2 p-4 ${isError ? "border-red-600 bg-red-50" : isComplete ? "border-emerald-600 bg-emerald-50" : "border-[#076D74] bg-slate-50"}`} data-testid="sap-draft-progress" aria-live="polite">
    <div className="flex items-start gap-3">
      {isError ? <AlertCircle className="mt-0.5 shrink-0 text-red-700" size={18} data-testid="sap-draft-progress-error-icon" /> : isComplete ? <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-700" size={18} data-testid="sap-draft-progress-complete-icon" /> : <Loader2 className="mt-0.5 shrink-0 animate-spin text-[#076D74]" size={18} data-testid="sap-draft-progress-loading-icon" />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-3">
          <p className="text-sm font-semibold text-slate-900" data-testid="sap-draft-progress-message">{progress.message}</p>
          {elapsed ? (
            <span className={`inline-flex items-center gap-1 text-[11px] font-mono tabular-nums ${isError ? "text-red-800" : isComplete ? "text-emerald-800" : "text-[#076D74]"}`} data-testid="sap-draft-progress-elapsed">
              <Clock size={11} /> {elapsed}{!isComplete && !isError ? " · running" : ""}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-slate-600" data-testid="sap-draft-progress-detail">{isError ? "No further SAP action was attempted. Review the message below before retrying." : isComplete ? `The SAP draft is available for finance review${elapsed ? ` · completed in ${elapsed}` : ""}.` : `Step ${Math.min(activeIndex + 1, STAGES.length)} of ${STAGES.length} · This can take 1–3 minutes on slow SAP tenants. Keep this page open.`}</p>
      </div>
    </div>
    <Progress value={value} className="mt-4 bg-slate-200 [&>div]:bg-[#076D74]" data-testid="sap-draft-progress-bar" />
    <ol className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="sap-draft-progress-steps">{STAGES.map(([stage, label], index) => {
      const done = isComplete || index < activeIndex;
      const current = !isError && index === activeIndex;
      return <li key={stage} className={`flex items-center gap-2 text-xs ${done ? "text-emerald-800" : current ? "font-semibold text-[#076D74]" : "text-slate-500"}`} data-testid={`sap-draft-progress-step-${stage}`}>{done ? <CheckCircle2 size={14} /> : current ? <Loader2 size={14} className="animate-spin" /> : <Circle size={14} />}{label}</li>;
    })}</ol>
    {isError && <p role="alert" className="mt-4 border-l-2 border-red-600 bg-white p-3 text-sm text-red-900" data-testid="sap-draft-progress-error">{progress.error || "SAP did not provide a detailed error. The invoice was not completed; review the draft and try again."}</p>}
  </section>;
};